import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import Sortable from 'sortablejs';
import { GetSiteGQL, GetPartnerPagesGQL, GetSiteNavigationGQL, UpdateSiteNavigationGQL } from '../../graphql/graphql';
import { ToastService } from '../../shared/toast/toast.service';

interface NavItem {
  id: string;
  label: string;
  type: 'partnerPage' | 'url' | 'saltusPage';
  pageId?: string;
  url?: string;
  order: number;
  redirectToChild?: boolean;
  children?: NavItem[];
}

interface SaltusPage {
  title: string;
  slug: string;
  url: string;
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: false
})
export class NavigationComponent implements OnInit, OnDestroy {
  @ViewChild('headerList') headerListEl?: ElementRef<HTMLElement>;
  @ViewChild('footerList') footerListEl?: ElementRef<HTMLElement>;

  siteUrl = '';
  siteId = '';
  activeTab: 'header' | 'footer' = 'header';
  headerItems: NavItem[] = [];
  footerItems: NavItem[] = [];
  availablePages: any[] = [];
  saltusPages: SaltusPage[] = [];
  saving = false;
  loading = true;

  private fixedHeaderUrls = new Set(['/', '/insights', '/contact']);

  // Modal state
  showModal = false;
  editingItem: NavItem | null = null;
  editingSection: 'header' | 'footer' = 'header';
  modalForm = { label: '', type: 'partnerPage' as 'partnerPage' | 'url' | 'saltusPage', pageId: '', url: '', redirectToChild: false };

  private sortableInstances: Sortable[] = [];
  private initScheduled = false;

  constructor(
    private route: ActivatedRoute,
    private getSiteGQL: GetSiteGQL,
    private getPartnerPagesGQL: GetPartnerPagesGQL,
    private getSiteNavigationGQL: GetSiteNavigationGQL,
    private updateSiteNavigationGQL: UpdateSiteNavigationGQL,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.siteUrl = this.route.parent?.snapshot.params['url'] || '';
    this.loadSiteData();
  }

  ngOnDestroy() {
    this.destroySortables();
  }

  loadSiteData() {
    if (!this.siteUrl) return;
    this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
      if (result.data?.site) {
        this.siteId = result.data.site._id!;
        this.loadPages();
        this.loadNavigation();
      }
    });
  }

  loadPages() {
    this.getPartnerPagesGQL.watch({ variables: { siteId: this.siteId }, fetchPolicy: 'network-only' }).valueChanges.subscribe(result => {
      if (result.data?.partnerPages) {
        this.availablePages = result.data.partnerPages.filter(p => p.type !== 'article' && p.published && !p.hidden && p.slug !== '/' && p.slug !== '/404' && p.slug !== '404').map(p => ({ ...p })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
    });
  }

  loadNavigation() {
    this.getSiteNavigationGQL.watch({ variables: { url: this.siteUrl, categoryId: '' }, fetchPolicy: 'network-only' }).valueChanges.subscribe(result => {
      const site = result.data?.site;
      if (site?.navigation) {
        this.headerItems = (site.navigation.header || []).map((item: any) => this.cleanNavItem(item));
        this.footerItems = (site.navigation.footer || []).map((item: any) => this.cleanNavItem(item));
      }
      this.ensureFixedHeaderItems();
      this.loading = false;
      if (site?.menus) {
        this.saltusPages = [];
        site.menus.forEach((menu: any) => {
          menu.links?.forEach((link: any) => {
            let localPath = `/${link.slug}`;
            if (link.url) {
              try { localPath = new URL(link.url).pathname; } catch {}
            }
            this.saltusPages.push({ title: link.title, slug: link.slug, url: localPath });
          });
        });
      }
      this.scheduleSortableInit();
    });
  }

  private cleanNavItem(item: any): NavItem {
    return {
      id: item.id,
      label: item.label,
      type: item.type,
      pageId: item.pageId || undefined,
      url: item.url || undefined,
      order: item.order,
      redirectToChild: item.redirectToChild || false,
      children: item.children?.map((c: any) => this.cleanNavItem(c)) || []
    };
  }

  scheduleSortableInit() {
    this.destroySortables();
    if (!this.initScheduled) {
      this.initScheduled = true;
      setTimeout(() => {
        this.initScheduled = false;
        this.initAllSortables();
      });
    }
  }

  private initAllSortables() {
    this.initHeaderSortables();
    this.initFooterSortable();
  }

  private initHeaderSortables() {
    const el = this.headerListEl?.nativeElement;
    if (!el) return;

    const onDragStart = () => el.classList.add('is-dragging');
    const onDragEnd = () => el.classList.remove('is-dragging');

    // Top-level sortable
    const topLevel = Sortable.create(el, {
      animation: 150,
      handle: '.drag-handle-parent,.drag-handle-child',
      ghostClass: 'sortable-ghost',
      group: { name: 'header-nav', pull: true, put: true },
      onStart: onDragStart,
      onAdd: (evt) => {
        onDragEnd();
        const itemId = (evt.item as HTMLElement).dataset['itemId'];
        if (!itemId) return;

        // Revert DOM
        evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex!] || null);

        // Remove from current position (it's a child being unnested)
        const item = this.removeItemById(itemId);
        if (!item) return;
        item.children = [];
        const insertIdx = Math.min(evt.newIndex ?? this.headerItems.length, this.headerItems.length);
        this.headerItems.splice(insertIdx, 0, item);
        this.reorder(this.headerItems);
        this.scheduleSortableInit();
      },
      onEnd: (evt) => {
        onDragEnd();
        // Revert DOM — we handle state ourselves
        const parent = evt.from;
        if (evt.oldIndex !== undefined && evt.item) {
          parent.removeChild(evt.item);
          if (parent.children[evt.oldIndex]) {
            parent.insertBefore(evt.item, parent.children[evt.oldIndex]);
          } else {
            parent.appendChild(evt.item);
          }
        }

        if (evt.from === evt.to) {
          // Simple reorder within top level
          if (evt.oldIndex !== undefined && evt.newIndex !== undefined && evt.oldIndex !== evt.newIndex) {
            const item = this.headerItems.splice(evt.oldIndex, 1)[0];
            this.headerItems.splice(evt.newIndex, 0, item);
            this.reorder(this.headerItems);
          }
        }
        // Re-enforce fixed item positions after any drag
        this.ensureFixedHeaderItems();
        // If moved to a child zone, onAdd on that zone handles it
        this.scheduleSortableInit();
      }
    });
    this.sortableInstances.push(topLevel);

    // Child sortables
    const childZones = el.querySelectorAll('.child-drop-zone');
    childZones.forEach((zone, zoneIdx) => {
      const s = Sortable.create(zone as HTMLElement, {
        animation: 150,
        handle: '.drag-handle-child',
        ghostClass: 'sortable-ghost',
        group: { name: 'header-nav', pull: true, put: true },
        onStart: onDragStart,
        onAdd: (evt) => {
          onDragEnd();
          const itemId = (evt.item as HTMLElement).dataset['itemId'];
          if (!itemId) return;

          // Revert DOM before Angular re-renders
          evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex!] || null);

          // Find and remove the item from its current position
          const item = this.removeItemById(itemId);
          if (!item) return;

          // Add as child of the target parent
          const targetParent = this.headerItems[zoneIdx];
          if (!targetParent || targetParent.id === itemId) return;
          if (!targetParent.children) targetParent.children = [];
          item.children = undefined; // strip children — max 1 level
          const insertIdx = Math.min(evt.newIndex ?? targetParent.children.length, targetParent.children.length);
          targetParent.children.splice(insertIdx, 0, item);
          this.reorder(targetParent.children);
          this.scheduleSortableInit();
        },
        onEnd: (evt) => {
          onDragEnd();
          if (evt.from === evt.to) {
            // Reorder within same child list
            const parent = this.headerItems[zoneIdx];
            if (parent.children && evt.oldIndex !== undefined && evt.newIndex !== undefined && evt.oldIndex !== evt.newIndex) {
              // Revert DOM
              evt.from.removeChild(evt.item);
              if (evt.from.children[evt.oldIndex]) {
                evt.from.insertBefore(evt.item, evt.from.children[evt.oldIndex]);
              } else {
                evt.from.appendChild(evt.item);
              }

              const child = parent.children.splice(evt.oldIndex, 1)[0];
              parent.children.splice(evt.newIndex, 0, child);
              this.reorder(parent.children);
            }
          }
          // Cross-list moves are handled by onAdd on the receiving list
          this.scheduleSortableInit();
        }
      });
      this.sortableInstances.push(s);
    });
  }

  private removeItemById(id: string): NavItem | null {
    // Check top level
    const topIdx = this.headerItems.findIndex(i => i.id === id);
    if (topIdx !== -1) {
      return this.headerItems.splice(topIdx, 1)[0];
    }
    // Check children
    for (const parent of this.headerItems) {
      if (parent.children) {
        const childIdx = parent.children.findIndex(c => c.id === id);
        if (childIdx !== -1) {
          const removed = parent.children.splice(childIdx, 1)[0];
          this.reorder(parent.children);
          return removed;
        }
      }
    }
    return null;
  }


  private initFooterSortable() {
    const el = this.footerListEl?.nativeElement;
    if (!el) return;
    const s = Sortable.create(el, {
      animation: 150,
      handle: '.drag-handle',
      ghostClass: 'sortable-ghost',
      onEnd: (evt) => {
        if (evt.oldIndex !== undefined && evt.newIndex !== undefined && evt.oldIndex !== evt.newIndex) {
          const item = this.footerItems.splice(evt.oldIndex, 1)[0];
          this.footerItems.splice(evt.newIndex, 0, item);
          this.reorder(this.footerItems);
        }
      }
    });
    this.sortableInstances.push(s);
  }

  private destroySortables() {
    this.sortableInstances.forEach(s => s.destroy());
    this.sortableInstances = [];
  }

  private reorder(items: NavItem[]) {
    items.forEach((item, i) => item.order = i);
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 10);
  }

  // Modal actions
  openAddModal(section: 'header' | 'footer') {
    this.editingItem = null;
    this.editingSection = section;
    this.modalForm = { label: '', type: 'partnerPage', pageId: '', url: '', redirectToChild: false };
    (this as any)._addChildParentIndex = undefined;
    this.showModal = true;
  }

  openEditModal(item: NavItem, section: 'header' | 'footer') {
    this.editingItem = item;
    this.editingSection = section;
    let url = item.url || '';
    // Convert legacy full external URLs to local paths for saltusPage items
    if (item.type === 'saltusPage' && url.startsWith('http')) {
      try { url = new URL(url).pathname; } catch {}
    }
    this.modalForm = { label: item.label, type: item.type, pageId: item.pageId || '', url, redirectToChild: item.redirectToChild || false };
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.editingItem = null;
    (this as any)._addChildParentIndex = undefined;
  }

  saveModal() {
    if (!this.modalForm.label) return;
    const items = this.editingSection === 'header' ? this.headerItems : this.footerItems;

    if (this.editingItem) {
      this.editingItem.label = this.modalForm.label;
      this.editingItem.type = this.modalForm.type;
      this.editingItem.pageId = this.modalForm.type === 'partnerPage' ? this.modalForm.pageId : undefined;
      this.editingItem.url = this.modalForm.type === 'url' || this.modalForm.type === 'saltusPage' ? this.modalForm.url : undefined;
      this.editingItem.redirectToChild = this.modalForm.redirectToChild;
    } else {
      const newItem: NavItem = {
        id: this.generateId(),
        label: this.modalForm.label,
        type: this.modalForm.type,
        pageId: this.modalForm.type === 'partnerPage' ? this.modalForm.pageId : undefined,
        url: this.modalForm.type === 'url' || this.modalForm.type === 'saltusPage' ? this.modalForm.url : undefined,
        order: items.length,
        redirectToChild: this.modalForm.redirectToChild,
        children: this.editingSection === 'header' ? [] : undefined
      };
      if (this.editingSection === 'header') {
        // Insert before the fixed trailing items (Insights, Contact)
        const insertIdx = items.length - 2;
        items.splice(insertIdx, 0, newItem);
        this.reorder(items);
      } else {
        items.push(newItem);
      }
    }
    this.closeModal();
    this.scheduleSortableInit();
  }

  removeItem(items: NavItem[], index: number) {
    if (items === this.headerItems && this.isFixedItem(items[index])) return;
    items.splice(index, 1);
    this.reorder(items);
    this.scheduleSortableInit();
  }

  // Header nesting via buttons (kept as alternative to drag)
  nestItem(parentIndex: number, childIndex: number) {
    if (parentIndex <= 0) return;
    const child = this.headerItems.splice(childIndex, 1)[0];
    child.children = undefined;
    const target = this.headerItems[parentIndex - 1];
    if (!target.children) target.children = [];
    target.children.push(child);
    this.reorder(this.headerItems);
    this.reorder(target.children);
    this.scheduleSortableInit();
  }

  unnestItem(parentIndex: number, childIndex: number) {
    const parent = this.headerItems[parentIndex];
    if (!parent.children) return;
    const child = parent.children.splice(childIndex, 1)[0];
    child.children = [];
    this.headerItems.splice(parentIndex + 1, 0, child);
    this.reorder(this.headerItems);
    this.reorder(parent.children);
    this.scheduleSortableInit();
  }

  addChildItem(parentIndex: number) {
    this.editingItem = null;
    this.editingSection = 'header';
    this.modalForm = { label: '', type: 'partnerPage', pageId: '', url: '', redirectToChild: false };
    (this as any)._addChildParentIndex = parentIndex;
    this.showModal = true;
  }

  saveModalChild() {
    if (!this.modalForm.label) return;
    const parentIndex = (this as any)._addChildParentIndex;
    if (parentIndex !== undefined) {
      const parent = this.headerItems[parentIndex];
      if (!parent.children) parent.children = [];
      parent.children.push({
        id: this.generateId(),
        label: this.modalForm.label,
        type: this.modalForm.type,
        pageId: this.modalForm.type === 'partnerPage' ? this.modalForm.pageId : undefined,
        url: this.modalForm.type === 'url' || this.modalForm.type === 'saltusPage' ? this.modalForm.url : undefined,
        order: parent.children.length,
        redirectToChild: this.modalForm.redirectToChild
      });
      delete (this as any)._addChildParentIndex;
      this.closeModal();
      this.scheduleSortableInit();
      return;
    }
    this.saveModal();
  }

  getPageName(pageId: string): string {
    const page = this.availablePages.find(p => p._id === pageId);
    return page ? page.name : pageId;
  }

  onPageSelected() {
    if (this.modalForm.type === 'partnerPage' && this.modalForm.pageId) {
      const page = this.availablePages.find(p => p._id === this.modalForm.pageId);
      if (page && !this.modalForm.label) {
        this.modalForm.label = page.name;
      }
    }
  }

  onSaltusPageSelected() {
    if (this.modalForm.type === 'saltusPage' && this.modalForm.url) {
      const page = this.saltusPages.find(p => p.url === this.modalForm.url);
      if (page && !this.modalForm.label) {
        this.modalForm.label = page.title;
      }
    }
  }

  isFixedItem(item: NavItem): boolean {
    return item.type === 'url' && !!item.url && this.fixedHeaderUrls.has(item.url);
  }

  private ensureFixedHeaderItems() {
    const fixed = [
      { label: 'Home', url: '/' },
      { label: 'Insights', url: '/insights' },
      { label: 'Contact', url: '/contact' },
    ];
    for (const f of fixed) {
      if (!this.headerItems.some(i => i.url === f.url)) {
        this.headerItems.push({ id: this.generateId(), label: f.label, type: 'url', url: f.url, order: 0, children: [] });
      }
    }
    // Ensure Home is first, Insights & Contact are last
    const home = this.headerItems.find(i => i.url === '/')!;
    const insights = this.headerItems.find(i => i.url === '/insights')!;
    const contact = this.headerItems.find(i => i.url === '/contact')!;
    const rest = this.headerItems.filter(i => !this.isFixedItem(i));
    this.headerItems = [home, ...rest, insights, contact];
    this.reorder(this.headerItems);
  }

  private getPageSlug(pageId: string): string {
    const page = this.availablePages.find(p => p._id === pageId);
    const slug = page?.slug || '';
    const parts = slug.replace(/^\//, '').split('/');
    return parts[parts.length - 1];
  }

  private resolveItemUrl(item: NavItem, parentPath: string): string {
    if (item.type === 'partnerPage' && item.pageId) {
      const slug = this.getPageSlug(item.pageId);
      return parentPath ? `${parentPath}/${slug}` : `/${slug}`;
    }
    let url = item.url || '';
    // Convert legacy full external URLs to local paths
    if (item.type === 'saltusPage' && url.startsWith('http')) {
      try { url = new URL(url).pathname; } catch {}
    }
    return url;
  }

  // Save
  save() {
    if (!this.siteId) return;
    this.saving = true;

    const toInput = (item: NavItem, parentPath: string = ''): any => {
      const url = this.resolveItemUrl(item, parentPath);
      return {
        id: item.id,
        label: item.label,
        type: item.type,
        pageId: item.pageId || null,
        url: url || null,
        order: item.order,
        redirectToChild: item.redirectToChild || false,
        children: item.children?.map(child => toInput(child, url)) || []
      };
    };

    const navigation = {
      header: this.headerItems.map(item => toInput(item)),
      footer: this.footerItems.map(item => toInput(item))
    };

    this.updateSiteNavigationGQL.mutate({ variables: { siteId: this.siteId, navigation } }).subscribe({
      next: () => {
        this.saving = false;
        this.toast.success('Navigation saved');
      },
      error: (err) => {
        this.saving = false;
        console.error('Error saving navigation:', err);
        this.toast.error('Error saving navigation');
      }
    });
  }
}
